import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { CreateWorkOrderRequest, WorkOrder } from '../../services/manufacturing.service';
import { inventoryService } from '../../services/inventory.service';
import {
  Field,
  FormActions,
  FormCard,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { engineeringBomApi, RequirementTable } from './BomStructureModal';
import { PlantField, plantPayload, usePlantChoice } from '../common/PlantField';

export type WorkOrderFormValues = CreateWorkOrderRequest & { status?: WorkOrder['status'] };

interface WorkOrderFormProps {
  // When set, the form edits this work order; otherwise it creates a new one
  workOrder?: WorkOrder;
  defaultWorkOrderNumber?: string;
  onSubmit: (values: WorkOrderFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
}

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrder['status'], string> = {
  draft: 'Draft',
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

// Mirrors the status transitions the API accepts
const STATUS_TRANSITIONS: Record<WorkOrder['status'], WorkOrder['status'][]> = {
  draft: ['planned', 'cancelled'],
  planned: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'on_hold', 'cancelled'],
  on_hold: ['in_progress', 'cancelled'],
  completed: [],
  cancelled: [],
};

const toDateInput = (value?: string) => (value ? String(value).slice(0, 10) : '');

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  workOrder,
  defaultWorkOrderNumber = '',
  onSubmit,
  onCancel,
  isPending,
}) => {
  const isEdit = !!workOrder;
  const plant = usePlantChoice();
  const [plantAsked, setPlantAsked] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<WorkOrderFormValues>({
    defaultValues: workOrder
      ? {
          workOrderNumber: workOrder.workOrderNumber || '',
          productId: workOrder.productId || '',
          plannedQuantity: toNumber(workOrder.plannedQuantity) || 1,
          priority: workOrder.priority || 'medium',
          status: workOrder.status,
          plannedStartDate: toDateInput(workOrder.plannedStartDate),
          plannedEndDate: toDateInput(workOrder.plannedEndDate),
          productionLine: workOrder.productionLine || '',
          batchNumber: workOrder.batchNumber || '',
          notes: workOrder.notes || '',
          estimatedCost: toNumber(workOrder.estimatedCost),
          laborHours: toNumber(workOrder.laborHours),
          machineHours: toNumber(workOrder.machineHours),
          salesOrderReference: workOrder.salesOrderReference || '',
          customerReference: workOrder.customerReference || '',
        }
      : {
          workOrderNumber: defaultWorkOrderNumber,
          productId: '',
          plannedQuantity: 1,
          priority: 'medium',
          plannedStartDate: '',
          plannedEndDate: '',
          productionLine: '',
          batchNumber: '',
          notes: '',
          estimatedCost: 0,
          laborHours: 0,
          machineHours: 0,
          salesOrderReference: '',
          customerReference: '',
        },
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => inventoryService.getProducts(),
  });

  // A new work order can take its materials from an engineering BOM of the product
  const productId = watch('productId');
  const bomHeaderId = watch('bomHeaderId');
  const plannedQuantity = Number(watch('plannedQuantity'));
  const plannedStart = watch('plannedStartDate');
  const { data: boms = [] } = useQuery({
    queryKey: ['engineeringBoms', 'usable', productId],
    queryFn: () => engineeringBomApi.usable(productId),
    enabled: !isEdit && !!productId,
  });
  // The BOM belongs to the product: another product means choosing again, and a product with one BOM takes it
  useEffect(() => {
    if (isEdit) return;
    const current = getValues('bomHeaderId');
    if (current && !boms.some((bom) => bom.id === current)) setValue('bomHeaderId', '');
    else if (!current && boms.length === 1) setValue('bomHeaderId', boms[0].id);
  }, [boms, isEdit, getValues, setValue]);
  const { data: explosion, error: explosionError } = useQuery({
    queryKey: ['engineeringBoms', 'explosion', bomHeaderId, plannedQuantity, plannedStart],
    queryFn: () => engineeringBomApi.explosion(bomHeaderId as string, plannedQuantity, plannedStart ? String(plannedStart).slice(0, 10) : undefined),
    enabled: !isEdit && !!bomHeaderId && Number.isFinite(plannedQuantity) && plannedQuantity > 0,
    retry: false,
  });

  const statusOptions: WorkOrder['status'][] = workOrder
    ? [workOrder.status, ...STATUS_TRANSITIONS[workOrder.status]]
    : [];

  const submit = (values: WorkOrderFormValues) => {
    setPlantAsked(true);
    if (!isEdit && plant.missing) return;
    onSubmit({
      ...values,
      ...(isEdit ? {} : plantPayload(plant.plantId)),
      bomHeaderId: values.bomHeaderId || undefined,
      plannedQuantity: toNumber(values.plannedQuantity),
      estimatedCost: toNumber(values.estimatedCost),
      laborHours: toNumber(values.laborHours),
      machineHours: toNumber(values.machineHours),
    });
  };

  const fieldClass = (hasError: boolean) => cn(inputClass, hasError && inputErrorClass);

  return (
    <form onSubmit={handleSubmit(submit, () => setPlantAsked(true))} noValidate>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <FormCard title="Order details" description="What is being produced, and how much.">
        <Field
          label="Work Order Number"
          htmlFor="wo-number"
          error={errors.workOrderNumber?.message}
          hint={isEdit ? 'The order number cannot be changed.' : 'Leave empty to take the next number of the year, such as WO/26-27/0001.'}
        >
          <input
            {...register('workOrderNumber', { maxLength: { value: 50, message: 'Keep the number within 50 characters' } })}
            type="text"
            id="wo-number"
            readOnly={isEdit}
            placeholder={isEdit ? undefined : 'Automatic'}
            className={cn(fieldClass(!!errors.workOrderNumber), 'font-mono')}
          />
        </Field>

        <Field label="Priority" htmlFor="wo-priority">
          <select {...register('priority')} id="wo-priority" className={inputClass}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </Field>

        <Field
          label="Product"
          htmlFor="wo-product"
          required
          error={errors.productId?.message}
        >
          <select
            {...register('productId', { required: 'Product is required' })}
            id="wo-product"
            className={fieldClass(!!errors.productId)}
          >
            <option value="">{productsLoading ? 'Loading products...' : 'Select a product'}</option>
            {/* Keep the current product selectable even before the list has loaded */}
            {workOrder?.product && !products.some((p) => p.id === workOrder.productId) && (
              <option value={workOrder.productId}>
                {workOrder.product.name} ({workOrder.product.sku})
              </option>
            )}
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.sku})
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Planned Quantity"
          htmlFor="wo-quantity"
          required
          error={errors.plannedQuantity?.message}
        >
          <input
            {...register('plannedQuantity', {
              required: 'Planned quantity is required',
              min: { value: 0.001, message: 'Quantity must be greater than zero' },
              valueAsNumber: true,
            })}
            type="number"
            id="wo-quantity"
            min="0.001"
            step="0.001"
            className={fieldClass(!!errors.plannedQuantity)}
          />
        </Field>

        {!isEdit && (
          <Field
            label="Bill of materials"
            htmlFor="wo-bom"
            className="sm:col-span-2"
            hint={
              !productId
                ? 'Choose the product first.'
                : boms.length === 0
                  ? 'This product has no approved BOM. The work order will have no materials.'
                  : 'The materials of the work order are worked out from this BOM.'
            }
          >
            <select {...register('bomHeaderId')} id="wo-bom" disabled={!productId || boms.length === 0} className={inputClass}>
              <option value="">No BOM: no materials</option>
              {boms.map((bom) => (
                <option key={bom.id} value={bom.id}>
                  {bom.bomNumber} · version {bom.version}
                  {bom.revision} · {bom.lines} component(s)
                </option>
              ))}
            </select>
          </Field>
        )}

        {!isEdit && bomHeaderId && (explosion || explosionError) && (
          <div className="space-y-2 sm:col-span-2">
            {explosionError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">{getApiErrorMessage(explosionError, 'The BOM could not be exploded.')}</p>}
            {explosion?.warnings.map((warning) => (
              <p key={warning} className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
                {warning}
              </p>
            ))}
            {explosion && (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Materials this work order will issue</p>
                <RequirementTable rows={explosion.issueList} empty="This BOM has no components." />
              </div>
            )}
          </div>
        )}

        {isEdit && (
          <Field
            label="Status"
            htmlFor="wo-status"
            hint="Only the next allowed statuses are listed."
          >
            <select {...register('status')} id="wo-status" className={inputClass}>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {WORK_ORDER_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </Field>
        )}
      </FormCard>

      <FormCard title="Schedule" description="When and where production runs.">
        <Field
          label="Planned Start Date"
          htmlFor="wo-start"
          required
          error={errors.plannedStartDate?.message}
        >
          <input
            {...register('plannedStartDate', { required: 'Start date is required' })}
            type="date"
            id="wo-start"
            className={fieldClass(!!errors.plannedStartDate)}
          />
        </Field>

        <Field
          label="Planned End Date"
          htmlFor="wo-end"
          required
          error={errors.plannedEndDate?.message}
        >
          <input
            {...register('plannedEndDate', {
              required: 'End date is required',
              validate: (value) =>
                !getValues('plannedStartDate') ||
                value >= getValues('plannedStartDate') ||
                'End date cannot be before the start date',
            })}
            type="date"
            id="wo-end"
            className={fieldClass(!!errors.plannedEndDate)}
          />
        </Field>

        <Field label="Production Line" htmlFor="wo-line">
          <input
            {...register('productionLine')}
            type="text"
            id="wo-line"
            className={inputClass}
            placeholder="e.g. Line 2"
          />
        </Field>

        <Field label="Batch Number" htmlFor="wo-batch">
          <input
            {...register('batchNumber')}
            type="text"
            id="wo-batch"
            className={inputClass}
            placeholder="e.g. B-2026-014"
          />
        </Field>
      </FormCard>

      <FormCard title="Cost and effort" description="Budget and hours planned for this order.">
        <Field
          label="Estimated Cost (₹)"
          htmlFor="wo-cost"
          error={errors.estimatedCost?.message}
          className="sm:col-span-2"
        >
          <input
            {...register('estimatedCost', {
              min: { value: 0, message: 'Cost cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            id="wo-cost"
            min="0"
            step="0.01"
            className={fieldClass(!!errors.estimatedCost)}
          />
        </Field>

        <Field label="Labor Hours" htmlFor="wo-labor" error={errors.laborHours?.message}>
          <input
            {...register('laborHours', {
              min: { value: 0, message: 'Hours cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            id="wo-labor"
            min="0"
            step="0.01"
            className={fieldClass(!!errors.laborHours)}
          />
        </Field>

        <Field label="Machine Hours" htmlFor="wo-machine" error={errors.machineHours?.message}>
          <input
            {...register('machineHours', {
              min: { value: 0, message: 'Hours cannot be negative' },
              valueAsNumber: true,
            })}
            type="number"
            id="wo-machine"
            min="0"
            step="0.01"
            className={fieldClass(!!errors.machineHours)}
          />
        </Field>
      </FormCard>

      <FormCard title="References and notes" description="Link this order to a sale or customer.">
        {!isEdit && <PlantField id="wo-plant" scope={plant.scope} value={plant.plantId} onChange={plant.setPlantId} showError={plantAsked} />}
        <Field label="Sales Order Reference" htmlFor="wo-so-ref">
          <input
            {...register('salesOrderReference')}
            type="text"
            id="wo-so-ref"
            className={inputClass}
            placeholder="e.g. SO-2024-001"
          />
        </Field>

        <Field label="Customer Reference" htmlFor="wo-customer-ref">
          <input
            {...register('customerReference')}
            type="text"
            id="wo-customer-ref"
            className={inputClass}
          />
        </Field>

        <Field label="Notes" htmlFor="wo-notes" className="sm:col-span-2">
          <textarea
            {...register('notes')}
            id="wo-notes"
            rows={2}
            className={inputClass}
            placeholder="Instructions for the production team..."
          />
        </Field>
      </FormCard>
      </div>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending
            ? isEdit
              ? 'Updating...'
              : 'Creating...'
            : isEdit
              ? 'Update Work Order'
              : 'Create Work Order'}
        </button>
      </FormActions>
    </form>
  );
};

export default WorkOrderForm;
